import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from './api';

/**
 * Seller and rider console data.
 *
 * Both are worked rather than read, so the caching rules are the opposite of
 * the buyer app's: nothing is allowed to be stale. A pharmacist looking at an
 * order a colleague accepted thirty seconds ago must not be able to accept it
 * again, and a rider must never see a handover screen for a run that has
 * already been completed on another device.
 *
 * Hence `staleTime: 0` everywhere, plus polling on the two lists that change
 * under you while you are looking at them.
 */

export const consoleKeys = {
  sellerOrders: () => ['seller', 'orders'] as const,
  sellerOrder: (reference: string) => ['seller', 'orders', reference] as const,
  sellerDebts: () => ['seller', 'debts'] as const,
  riderRuns: () => ['rider', 'runs'] as const,
  riderRun: (reference: string) => ['rider', 'runs', reference] as const,
};

// ---------------------------------------------------------------------------
// Seller
// ---------------------------------------------------------------------------

export function useSellerOrders() {
  return useQuery({
    queryKey: consoleKeys.sellerOrders(),
    queryFn: ({ signal }) => api.sellerOrders(undefined, signal),
    staleTime: 0,
    // New orders arrive while the pharmacist is looking at the screen, and the
    // accept window is fifteen minutes. Thirty seconds is the difference
    // between noticing and breaching.
    refetchInterval: 30_000,
  });
}

export function useSellerOrder(reference: string | null) {
  return useQuery({
    queryKey: consoleKeys.sellerOrder(reference ?? ''),
    queryFn: ({ signal }) => api.sellerOrder(reference as string, signal),
    enabled: reference !== null,
    staleTime: 0,
  });
}

export function useAcceptOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (reference: string) => api.acceptOrder(reference),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['seller'] }),
  });
}

export function usePackOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ reference, sealNumber }: { reference: string; sealNumber: string }) =>
      api.packOrder(reference, sealNumber),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['seller'] }),
  });
}

export function useDispatchOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      reference,
      riderId,
      idempotencyKey,
    }: {
      reference: string;
      riderId?: number;
      idempotencyKey: string;
    }) => api.dispatchOrder(reference, { riderId }, idempotencyKey),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['seller'] }),
  });
}

export function useSellerDebts() {
  return useQuery({
    queryKey: consoleKeys.sellerDebts(),
    queryFn: ({ signal }) => api.sellerDebtStatement(signal),
    staleTime: 0,
  });
}

// ---------------------------------------------------------------------------
// Rider
// ---------------------------------------------------------------------------

export function useRiderRuns() {
  return useQuery({
    queryKey: consoleKeys.riderRuns(),
    queryFn: ({ signal }) => api.riderRuns(signal),
    staleTime: 0,
    refetchInterval: 30_000,
  });
}

export function useRiderRun(reference: string | null) {
  return useQuery({
    queryKey: consoleKeys.riderRun(reference ?? ''),
    queryFn: ({ signal }) => api.riderRun(reference as string, signal),
    enabled: reference !== null,
    staleTime: 0,
    // A door capture clears while the rider is standing there. Polling is what
    // turns "waiting for payment" into "ask for their code" without the rider
    // having to work out that they should pull to refresh.
    refetchInterval: (query) =>
      query.state.data?.run.awaiting_payment ? 5_000 : false,
  });
}

export function useRiderArrive() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (reference: string) => api.riderArrive(reference),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['rider'] }),
  });
}

export function useRiderHandover() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      reference,
      code,
      recipientName,
    }: {
      reference: string;
      code: string;
      recipientName?: string;
    }) => api.riderHandover(reference, { code, recipientName }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['rider'] }),
  });
}
